import React, { createContext, useContext } from "react"
import {
  Box,
  Button,
  Divider,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react"
import { useDrag } from "react-dnd"
import Icon from "components/elements/Icon"
import chakraTheme from "theme/chakraTheme"
import { useEditorLayout } from "../EditorLayout"
import { TOOLBAR_CONFIG } from "./config"

// Measured on the first render, so the toolbar never draws at one size and then another
const BREAKPOINT_OPTIONS = { ssr: false }

/** The toolbar's size: compact below the xl breakpoint. */
export const useToolbarVariant = () => (
  (useBreakpointValue({ base: true, xl: false }, BREAKPOINT_OPTIONS) ?? true) ? "compact" : "default"
)

type ToolbarProps = {
  children: React.ReactNode
}

/** The floating toolbar, centred over the canvas between the panels. */
export const EditorToolbar = ({ children }: ToolbarProps) => {
  const { leftPanelOpen, panelWidths } = useEditorLayout()
  const variant = useToolbarVariant()

  const offset = ((leftPanelOpen ? panelWidths.left : 0) - panelWidths.right) / 2

  return (
    <Box
      position="absolute"
      top={0}
      left="50%"
      transform={`translateX(calc(-50% + ${offset}px))`}
      zIndex={999}
    >
      <Box
        bg="white"
        borderWidth={1}
        borderRadius="lg"
        position="absolute"
        shadow="lg"
        top={4}
        left="50%"
        transform="translateX(-50%)"
        zIndex={999}
        px={TOOLBAR_CONFIG.container.paddingX[variant]}
      >
        <HStack spacing={0} align="center" h={`${TOOLBAR_CONFIG.height[variant]}px`}>
          {children}
        </HStack>
      </Box>
    </Box>
  )
}

/** A line between groups; `invisible` keeps the spacing without the line. */
export const EditorToolbarSeparator = ({ invisible = false }: { invisible?: boolean }) => {
  const variant = useToolbarVariant()

  return (
    <Divider
      orientation="vertical"
      h={TOOLBAR_CONFIG.divider.height[variant]}
      mx={invisible
        ? TOOLBAR_CONFIG.actionGroup.invisibleDividerMargin[variant]
        : TOOLBAR_CONFIG.actionGroup.gap[variant]}
      opacity={invisible ? 0 : undefined}
    />
  )
}

const GroupContext = createContext<{ collapsed: boolean }>({ collapsed: false })

type BreakpointValue = keyof typeof chakraTheme["breakpoints"]

type GroupProps = {
  label: string
  icon?: React.ReactElement
  /** Below this breakpoint, the group's items move into a menu. */
  collapseBreakpoint?: BreakpointValue
  children: React.ReactNode
}

/** A group of toolbar items: buttons side by side, or a menu on narrow screens. */
export const EditorToolbarGroup = ({
  label, icon, collapseBreakpoint, children,
}: GroupProps) => {
  const variant = useToolbarVariant()
  const collapsed = useBreakpointValue(
    collapseBreakpoint
      ? { base: true, [collapseBreakpoint]: false }
      : { base: false },
    BREAKPOINT_OPTIONS,
  ) ?? false

  if (!collapsed) {
    return (
      <GroupContext.Provider value={{ collapsed }}>
        <HStack spacing={0}>{children}</HStack>
      </GroupContext.Provider>
    )
  }

  return (
    <GroupContext.Provider value={{ collapsed }}>
      <Menu>
        <Tooltip label={label} placement="bottom">
          <MenuButton
            as={Button}
            variant="ghost"
            h={`${TOOLBAR_CONFIG.height[variant]}px`}
            w="auto"
            aspectRatio="1/1"
            p={TOOLBAR_CONFIG.button.padding[variant]}
            rounded="none"
            color="gray.600"
          >
            <VStack
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={TOOLBAR_CONFIG.button.iconTextGap[variant]}
            >
              <HStack alignItems="center" justifyContent="center" gap={1}>
                {icon}
                <Icon icon="down-arrow" size={3} />
              </HStack>
              <Text fontSize={TOOLBAR_CONFIG.button.textSize[variant]} color="gray.700">{label}</Text>
            </VStack>
          </MenuButton>
        </Tooltip>
        <MenuList>{children}</MenuList>
      </Menu>
    </GroupContext.Provider>
  )
}

type ItemProps = {
  label: string
  icon: React.ReactElement
  onClick: () => void
  isDisabled?: boolean
  /** Makes the item draggable onto the canvas (react-dnd). */
  drag?: { type: string; item: object }
  tooltip?: string
}

/** A toolbar item: a button in an open group, a menu item in a collapsed one. */
export const EditorToolbarItem = ({
  label, icon, onClick, isDisabled = false, drag, tooltip,
}: ItemProps) => {
  const { collapsed } = useContext(GroupContext)
  const variant = useToolbarVariant()
  const canDrag = !!drag && !isDisabled

  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: drag?.type ?? "NONE",
    item: drag?.item ?? null,
    canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [drag?.type, drag?.item, canDrag])

  if (collapsed) {
    return (
      <MenuItem
        ref={drag ? dragRef : undefined}
        icon={icon}
        isDisabled={isDisabled}
        onClick={onClick}
        {...(drag && { opacity: isDragging ? 0.5 : 1 })}
      >
        {label}
      </MenuItem>
    )
  }

  return (
    <Tooltip label={tooltip ?? label} placement="bottom">
      <Button
        ref={drag ? dragRef : null}
        variant="ghost"
        onClick={onClick}
        isDisabled={isDisabled}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={TOOLBAR_CONFIG.button.iconTextGap[variant]}
        h={`${TOOLBAR_CONFIG.height[variant]}px`}
        w="auto"
        aspectRatio="1/1"
        p={TOOLBAR_CONFIG.button.padding[variant]}
        rounded="none"
        color="gray.600"
        opacity={isDragging ? 0.5 : 1}
        cursor="pointer"
      >
        {icon}
        <Text fontSize={TOOLBAR_CONFIG.button.textSize[variant]} color="gray.700">{label}</Text>
      </Button>
    </Tooltip>
  )
}
